// Contact Service
// Handles all contact and inquiry-related API calls

import authService from './Auth.services.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://the-live-cure-be.vercel.app/api';

class ContactService {
  // Submit Contact Inquiry (Public)
  async submitInquiry(inquiryData) {
    try {
      const response = await fetch(`${API_BASE_URL}/contact/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inquiryData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Submit inquiry error:', error);
      throw error;
    }
  }

  // Submit Appointment Request (Public)
  async submitAppointmentRequest(requestData) {
    try {
      const response = await fetch(`${API_BASE_URL}/contact/appointment-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit appointment request');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Submit appointment request error:', error);
      throw error;
    }
  }

  // Admin Contact APIs
  async getAllInquiries(filters = {}) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      // Build query string from filters
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/contact?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inquiries');
      }

      const data = await response.json();
      return data.result || data.inquiries || [];
    } catch (error) {
      console.error('Get all inquiries error:', error);
      throw error;
    }
  }

  async getInquiryById(inquiryId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inquiry');
      }

      const data = await response.json();
      return data.inquiry;
    } catch (error) {
      console.error('Get inquiry by ID error:', error);
      throw error;
    }
  }

  async updateInquiry(inquiryId, inquiryData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(inquiryData),
      });

      if (!response.ok) {
        throw new Error('Failed to update inquiry');
      }

      const data = await response.json();
      return data.inquiry;
    } catch (error) {
      console.error('Update inquiry error:', error);
      throw error;
    }
  }

  async updateInquiryStatus(inquiryId, status) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error('Failed to update inquiry status');
      }

      const data = await response.json();
      return data.inquiry;
    } catch (error) {
      console.error('Update inquiry status error:', error);
      throw error;
    }
  }

  async deleteInquiry(inquiryId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete inquiry');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete inquiry error:', error);
      throw error;
    }
  }

  async getInquiryStats() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inquiry statistics');
      }

      const data = await response.json();
      return data.result || data.stats;
    } catch (error) {
      console.error('Get inquiry stats error:', error);
      throw error;
    }
  }

  async respondToInquiry(inquiryId, responseData) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}/respond`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(responseData),
      });

      if (!response.ok) {
        throw new Error('Failed to send response');
      }

      const data = await response.json();
      return data.result || data.inquiry;
    } catch (error) {
      console.error('Respond to inquiry error:', error);
      throw error;
    }
  }

  async assignInquiry(inquiryId, userId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/${inquiryId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assignedTo: userId }),
      });

      if (!response.ok) {
        throw new Error('Failed to assign inquiry');
      }

      const data = await response.json();
      return data.result || data.inquiry;
    } catch (error) {
      console.error('Assign inquiry error:', error);
      throw error;
    }
  }

  async getUrgentInquiries(filters = {}) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/contact/urgent?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch urgent inquiries');
      }

      const data = await response.json();
      return data.result || data.inquiries || [];
    } catch (error) {
      console.error('Get urgent inquiries error:', error);
      throw error;
    }
  }

  async getInquiriesByStatus(status, filters = {}) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/contact/status/${status}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inquiries by status');
      }

      const data = await response.json();
      return data.result || data.inquiries || [];
    } catch (error) {
      console.error('Get inquiries by status error:', error);
      throw error;
    }
  }

  async getInquiriesByType(type, filters = {}) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/contact/type/${type}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inquiries by type');
      }

      const data = await response.json();
      return data.result || data.inquiries || [];
    } catch (error) {
      console.error('Get inquiries by type error:', error);
      throw error;
    }
  }

  async getRecentInquiries() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/recent`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recent inquiries');
      }

      const data = await response.json();
      return data.inquiries;
    } catch (error) {
      console.error('Get recent inquiries error:', error);
      throw error;
    }
  }

  async getNewInquiries() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/contact/new`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new inquiries');
      }

      const data = await response.json();
      return data.inquiries;
    } catch (error) {
      console.error('Get new inquiries error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const contactService = new ContactService();
export default contactService;
